export type TVideoPlacement = 'in-stream' | 'outstream' | 'ctv';
export type TVideoSource = 'upload' | 'vast';

/** What the browser could measure about a video. Library assets carry less. */
export interface IVideoFileMeta {
  width?: number;
  height?: number;
  durationSeconds?: number;
  sizeBytes?: number;
  codec?: string;
  frameRate?: number;
  hasAudio?: boolean;
}

export type TVastCheckKey =
  | 'version'
  | 'wrappers'
  | 'https'
  | 'media'
  | 'length'
  | 'vpaid';

/** Result of `POST /businesses/:id/creatives/vast/validate`. */
export interface IVastValidation {
  valid: boolean;
  /** Set when the tag could not be fetched or read as VAST at all. */
  error?: string;
  version?: string;
  wrapperDepth: number;
  https: boolean;
  mediaFile?: {
    url: string;
    type?: string;
    width?: number;
    height?: number;
  };
  durationSeconds?: number;
  hasVpaid: boolean;
  /** Only when the tag states one; VAST's `apiFramework` often does not. */
  vpaidVersion?: string;
  hasMraid: boolean;
  clickThroughUrl?: string;
  checks: { key: TVastCheckKey; passed: boolean }[];
}

export interface IDisplayBanner {
  /** Stable row id. Rejected files have no asset, so this is not the asset id. */
  id: string;
  name: string;
  width: number;
  height: number;
  sizeBytes?: number;
  /** Present only once the file passed its checks and was uploaded. */
  assetId?: string;
  url?: string;
  needsBorder?: boolean;
  /** Why the file was rejected. A rejected banner never launches. */
  rejection?: 'too-heavy' | 'unsupported-size' | 'unsupported-type';
  maxBytes?: number;
}

export type TNativeImageSlot = 'logo' | 'landscape' | 'square';

export interface INativeImage {
  name: string;
  width: number;
  height: number;
  assetId?: string;
  url?: string;
  /** Only ever a warning: the check is a heuristic. */
  textHeavy?: boolean;
  /** Set when the file does not fit this slot. Such a file is not uploaded. */
  rejected?: boolean;
}

export interface IDisplayAdCreative {
  uploadedCreative: File | null;
  selectedAssetId?: string;
  selectedAssetName?: string;
  selectedAssetUrl?: string;
  headline: string;
  callToAction: string;
  couponCode: string;
  creativeName?: string;
  banners?: IDisplayBanner[];
}

export interface IVideoAdCreative {
  uploadedVideo: File | null;
  selectedAssetId?: string;
  selectedAssetName?: string;
  selectedAssetUrl?: string;
  headline: string;
  callToAction: string;
  couponCode: string;
  creativeName?: string;
  source?: TVideoSource;
  placement?: TVideoPlacement;
  skippable?: boolean;
  fileMeta?: IVideoFileMeta;
  companionBannerUrl?: string;
  companionBannerName?: string;
  vastTagUrl?: string;
  vastValidation?: IVastValidation;
}

export interface INativeAdCreative {
  brandName: string;
  creativeName: string;
  headline: string;
  longHeadline: string;
  description: string;
  callToAction: string;
  images: Partial<Record<TNativeImageSlot, INativeImage>>;
}

export interface ITrafficSalesCreative {
  selectedFormat: string;
  landingPageUrl: string;
  displayAd: IDisplayAdCreative;
  richNotification: {
    uploadedImage: File | null;
    selectedAssetId?: string;
    selectedAssetName?: string;
    selectedAssetUrl?: string;
    headline: string;
    description: string;
    callToAction: string;
    couponCode: string;
  };
  videoAd: IVideoAdCreative;
  nativeAd: INativeAdCreative;
  smsNotification: {
    storeTargeting: string;
    messageContent: string;
    couponCode: string;
  };
}

export type IBrandAwarenessCreative = ITrafficSalesCreative;

export enum EObjective {
  TrafficSales = 'traffic-sales',
  BrandAwareness = 'brand-awareness',
}
